import { Component, computed, OnDestroy, OnInit, signal, Signal, TemplateRef, viewChild, ViewChild, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AwModalService } from '@services/modal/aw-modal.service';
import { DataSets, LineComponent } from '../line-chart/line.component';
import { AuthService } from '@services/auth/auth.service';
import { AccountService } from '@services/account/account.service';
import { CustomerService } from '@services/customer/customer.service';
import { HelperService } from '@services/helper/helper.service';
import { ChartService } from '@services/charts/chart.service';
import { HomeService } from '@services/home/home.service';
import { DashboardOwnerUser } from '@dashboard_models/dashboard-owner-user';
import { Subject } from 'rxjs';
import { RouterLink } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { ActionsService } from '@services/actions/actions.service';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { SortService } from '@services/sorting/sort.service';
import { OnboardingService } from '@services/onboarding/onboarding.service';
import { ContractExpiringListComponent } from './contract-expiring-list/contract-expiring-list.component';
import { ContractPerformanceListComponent } from './contract-performance-list/contract-performance-list.component';
import { SmallOverviewActionsCardComponent } from '@components/actions/small-overview-actions-card/small-overview-actions-card.component';
import { AwTooltipComponent } from '@components/misc/aw-tooltip/aw-tooltip.component';
import { FormsModule } from '@angular/forms';
import { OnboardingComponent } from './onboarding/onboarding.component';
import { LoadingComponent } from '../loading/loading.component';

import { LocalStorageService } from '@services/local-storage/local-storage.service';
import { MixpanelService } from '@services/mixpanel/mixpanel.service';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
dayjs.extend(advancedFormat);
import { FeatureService } from '@services/feature-toggle/feature.service';
import { getAuth } from 'firebase/auth';
import { ToastService } from '@services/helper/toast.service';
import { SidebarService } from '@services/sidebar/sidebar.service';
import { QuickPayoutModalComponent } from '@components/home/quick-payout-modal/quick-payout-modal.component';
import { CategorizeLocationsModalComponent } from '@components/home/categorize-locations-modal/categorize-locations-modal.component';
import { LocationService } from '@services/location/location.service';
import { environment } from 'environment';
import { IntroductionModalComponent } from './introduction-modal/introduction-modal.component';
import { Account, AW } from '@airwallet/types';
import { euCountries } from '@airwallet/data';
import { AwSwitchComponent } from '@components/misc/aw-switch/aw-switch.component';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrl: './home.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        LoadingComponent,
        OnboardingComponent,
        FormsModule,
        LineComponent,
        RouterLink,
        SmallOverviewActionsCardComponent,
        LottieComponent,
        ContractPerformanceListComponent,
        ContractExpiringListComponent,
        TranslateModule,
        QuickPayoutModalComponent,
        AwTooltipComponent,
        AwSwitchComponent,
        IntroductionModalComponent,
        CategorizeLocationsModalComponent
    ]
})
export class HomeComponent implements OnInit, OnDestroy {
    @ViewChild(LineComponent, { static: false }) line!: LineComponent;
    private readonly introductionModalTpl = viewChild.required<TemplateRef<unknown>>('introductionModal');
    private readonly categorizeLocationsModalTpl = viewChild.required<TemplateRef<unknown>>('categorizeLocationsModal');
    private readonly CATEGORIZE_MODAL_ID = 'categorize-locations-modal';
    private startupModalTimer: ReturnType<typeof setTimeout> | null = null;
    readonly uncategorizedLocations: WritableSignal<AW.Location.UncategorizedLocation[]> = signal([]);
    loadingCustomerDetails: Signal<boolean> = this.homeService.loadingCustomerDetails;
    customerDetails: Signal<AW.Account.Details | null> = this.homeService.customerDetails;
    onboardingState: Signal<AW.Account.OnboardingState | undefined> = this.onboardingService.onboardingState;
    availableFunds: WritableSignal<AW.BankAccount.AvailableFunds[]> = signal([]);
    availableFundsLabels: Signal<string[]> = computed(() => {
        const funds = this.availableFunds();
        if (funds.length > 0) {
            const fundLabels = [];
            for (const baFunds of funds) {
                fundLabels.push(this.helperService.localizeNumberWithCurrency(baFunds.availableFunds / 100, 2, 2, baFunds.currency));
            }
            return fundLabels;
        } else {
            return [this.helperService.localizeNumberWithCurrency(0)];
        }
    });
    showLoadingIndicatorForPayoutDetails = signal(true);
    viewOptionForCharts: 'week' | 'month' | 'year' = 'month';
    activeUsersData!: { combined: AW.Analytics.ActiveUsers; own: AW.Analytics.ActiveUsers };
    active_users_present = signal(0);
    totalRevenue: string = this.helperService.localizeNumberWithCurrency(0);
    private allDataSets!: AW.Analytics.ChartData;
    dataSets!: DataSets;
    showCombined: WritableSignal<boolean> = signal(false);
    showSmallLoadingIndicator = signal(false);
    persons: AW.Account.Persons = {
        representative: {
            first_name: '',
            last_name: '',
            title: ''
        },
        verified: false
    };
    user: DashboardOwnerUser = this.helperService.getDashboardOwnerUser();
    actions: AW.Operator.Action.Action[] = [];
    isSkeleton = signal(true);
    actionLoading = signal(false);
    fromDate!: number;
    toDate!: number;

    options: AnimationOptions = {
        // https://www.npmjs.com/package/ngx-lottie
        path: '../../../assets/animations/jumping_star.json'
    };
    fireCustomer: Signal<Account | null> = this.authService.customerFirestore;
    controlledUserActive!: boolean;
    shouldShowQuickPayout: Signal<boolean> = computed(() => {
        const firecustomer = this.fireCustomer();
        if (!firecustomer || firecustomer.float_owner) return false;

        const isUs = firecustomer.stripe_region === 'us' && firecustomer.country.toUpperCase() === 'US';
        const isEu = firecustomer.stripe_region === 'eu' && euCountries.includes(firecustomer.country.toUpperCase());
        const isGb = firecustomer.stripe_region === 'eu' && firecustomer.country.toUpperCase() === 'GB';
        return isUs || isEu || isGb;
    });
    shouldShowWeeklyPayoutCTA: Signal<boolean> = computed(() => {
        const firecustomer = this.fireCustomer();
        const showQuickPayout = this.shouldShowQuickPayout();
        const scheduleFeeHasBeenOverridden = firecustomer?.payout_settings?.schedule_fee_percentage === 0;
        if (!showQuickPayout && !scheduleFeeHasBeenOverridden) return false;
        if (!firecustomer?.payout_settings?.interval) return false;
        return firecustomer.payout_settings.interval !== 'weekly';
    });
    onboardingComplete: Signal<boolean> = this.onboardingService.onboardingComplete;
    companyName!: string;
    currency!: string;
    isDemo = environment.isDemo;
    isActionsEnabled: Signal<boolean> = this.featureService.getFeatureAccess('actions', 'route_access');
    private ngUnsubscribe = new Subject<void>();

    private auth = getAuth();

    constructor(
        protected authService: AuthService,
        private accountService: AccountService,
        private customerService: CustomerService,
        private helperService: HelperService,
        private chartService: ChartService,
        private toastService: ToastService,
        private homeService: HomeService,
        private translate: TranslateService,
        public actionService: ActionsService,
        private mixpanelService: MixpanelService,
        private onboardingService: OnboardingService,
        private localStorageService: LocalStorageService,
        protected featureService: FeatureService,
        protected sidebarService: SidebarService,
        protected awModal: AwModalService,
        private locationService: LocationService
    ) {
        dayjs().locale(this.translate.currentLang);
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
        if (this.startupModalTimer) clearTimeout(this.startupModalTimer);
    }

    async ngOnInit() {
        this.fromDate = dayjs().subtract(29, 'days').startOf('day').unix() + dayjs().utcOffset() * 60;
        this.controlledUserActive = this.helperService.isUserControlled();
        this.setSubscriptions();
        this.getActions();

        // Delay to make sure firecustomer signal is set before checking local storage. Cancelled on destroy
        // so a briefly double-mounted home view (cold login) doesn't fire this twice.
        this.startupModalTimer = setTimeout(() => this.openStartupModal(), 1000);
    }

    private async openStartupModal(): Promise<void> {
        if (this.isDemo && !this.localStorageService.getItem('introductionModalDismissed')) {
            await this.openIntroductionModal();
        } else if (!this.isDemo) {
            await this.openCategorizeLocationsModalIfNeeded();
        }
    }

    async openCategorizeLocationsModalIfNeeded(): Promise<void> {
        const role = this.authService.getRole();
        if ((role !== 'owner' && role !== 'editor') || this.authService.isAdmin()) return;

        const uncategorized = await this.locationService.getUncategorizedLocations(this.user.uid).catch(() => [] as AW.Location.UncategorizedLocation[]);
        if (!uncategorized.length) return;

        if (this.awModal.isOpen(this.CATEGORIZE_MODAL_ID)) return;

        this.uncategorizedLocations.set(uncategorized);
        const ref = this.awModal.open(this.categorizeLocationsModalTpl(), { id: this.CATEGORIZE_MODAL_ID, disableClose: true, size: 'lg', scrollable: true });
        await firstValueFrom(ref.closed);
    }

    async openIntroductionModal(): Promise<void> {
        const ref = this.awModal.open(this.introductionModalTpl());
        await firstValueFrom(ref.closed);
        this.removeIntroductionModal();
    }

    removeIntroductionModal() {
        console.log('Remove called');
        this.localStorageService.setItem('introductionModalDismissed', true);
    }

    async setSubscriptions() {
        await this.homeService.getCustomerDetails(this.user.uid).then(async () => {
            await this.onboardingService.getOnboardingStatus();
        });
        this.homeService.loadingCustomerDetails.set(false);
        this.handleVerification();

        if (this.customerDetails()?.business_type === 'company') {
            this.companyName = this.authService.customerFirestore()?.shallow_account_details?.name ?? '';
        }

        if (this.onboardingComplete() === true) {
            const persons = this.customerDetails()?.persons;
            if (persons) {
                const representative = this.accountService.getAccountRepresentative(persons);
                if (representative) {
                    this.persons = {
                        representative: {
                            first_name: representative.first_name ?? '',
                            last_name: representative.last_name ?? '',
                            title: representative.relationship?.title ?? ''
                        },
                        verified: true
                    };
                }
                // if requirements is found on any of the persons attached to the account, the verified state should be false.
                this.persons.verified = Object.values(persons).some(p => p.requirements) ? false : this.persons.verified;
            }

            this.getAvailableFunds();

            await this.chartService.getChartData(this.translate.currentLang, this.translate.instant('overview.today')).then(chartData => {
                this.allDataSets = chartData;
                this.showCombined.set(this.authService.isOperator());
                this.applyDataSets();
                if (this.line) {
                    this.line.setChart(this.dataSets ? this.dataSets.last30Days.line : []);
                    this.viewOptionForCharts = 'month';
                }
            });

            this.getActiveUserData();
        }
    }

    applyDataSets() {
        if (!this.allDataSets) return;
        this.dataSets = this.showCombined() && this.authService.isOperator() ? this.allDataSets.combined : this.allDataSets.customer;
    }

    onToggleCombined() {
        this.showCombined.set(!this.showCombined());
        this.applyDataSets();
        this[this.viewOptionForCharts](); // Calls either week(), month() or year()
    }

    goToStripeKYC() {
        if ((this.onboardingState() as any).is_andorra) {
            this.toastService.info(this.translate.instant('overview.is_andorra'));
            return;
        }
        this.showSmallLoadingIndicator.set(true);
        this.onboardingService.getStripeKYCUrl('/home').catch(error => {
            console.error(error);
            this.showSmallLoadingIndicator.set(false);
        });
    }

    async handleVerification() {
        // verify_email is a value in UNIX UTC seconds set on account creation.
        const verifyEmail = this.customerDetails()?.verify_email;
        if (verifyEmail) {
            if (this.controlledUserActive) {
                await this.homeService
                    .fetchUserAsAdmin()
                    .then(async userRecord => {
                        if (!userRecord.email_verified && dayjs().unix() > dayjs.unix(verifyEmail).add(24, 'hour').unix()) {
                            if (userRecord.email) {
                                this.helperService.verifyEmailToast(userRecord.email);
                            } else {
                                this.toastService.error(this.translate.instant('overview.no_email_found'), undefined, { disableTimeOut: true });
                            }
                        } else {
                            this.customerService.removeVerifyEmailTimestamp(this.user.uid); // fire and forget
                        }
                    })
                    .catch(err => {
                        console.error(err);
                    });
            } else if (!this.auth.currentUser?.emailVerified) {
                // If emailed not verified, and it is more than 24h since the account was created
                if (dayjs().unix() > dayjs.unix(verifyEmail).add(24, 'hour').unix()) {
                    if (this.auth.currentUser?.email) {
                        this.helperService.verifyEmailToast(this.auth.currentUser.email);
                    }
                }
            } else {
                this.customerService.removeVerifyEmailTimestamp(this.user.uid); // fire and forget
            }
        }
    }

    handleDateChange(value: string) {
        switch (value) {
            case 'week':
                this.week();
                this.mixpanelService.track('RoT 7 Days Filter', { filter_value: value });
                break;
            case 'month':
                this.month();
                this.mixpanelService.track('RoT 1 Month Filter', { filter_value: value });
                break;
            case 'year':
                this.year();
                this.mixpanelService.track('RoT 1 Year Filter', { filter_value: value });
                break;
        }
    }

    week() {
        this.setFromDateForChild(7);
        if (this.dataSets && this.line) {
            this.line.setChart(this.dataSets ? this.dataSets.last7Days.line : []);
        }
        this.viewOptionForCharts = 'week';
        this.setActiveUser();
        this.updateRevenueOverPeriod();
    }

    month() {
        this.setFromDateForChild(30);
        if (this.dataSets && this.line) {
            this.line.setChart(this.dataSets ? this.dataSets.last30Days.line : []);
        }
        this.viewOptionForCharts = 'month';
        this.setActiveUser();
        this.updateRevenueOverPeriod();
    }

    year() {
        this.setFromDateForChild(365);
        if (this.dataSets && this.line) {
            this.line.setChart(this.dataSets ? this.dataSets.last12Months.line : []);
        }
        this.viewOptionForCharts = 'year';
        this.setActiveUser();
        this.updateRevenueOverPeriod();
    }

    getActiveUserData() {
        this.chartService.getUserStats().then(users_stats => {
            this.activeUsersData = users_stats;
            this.setActiveUser();
        });
    }

    setActiveUser() {
        if (!this.activeUsersData) return;
        const period = this.viewOptionForCharts;
        this.active_users_present.set(this.showCombined() ? this.activeUsersData.combined[period] : this.activeUsersData.own[period]);
    }

    setFromDateForChild(days: number) {
        this.fromDate = dayjs().subtract(days, 'days').startOf('day').unix() + dayjs().utcOffset() * 60;
    }

    async getAvailableFunds() {
        const availableFunds: AW.BankAccount.AvailableFunds[] = await this.homeService.getAvailableFunds();
        this.availableFunds.set(availableFunds);
        setTimeout(() => {
            this.showLoadingIndicatorForPayoutDetails.set(false);
        }, 2000);
    }

    formatPendingAmount(baFunds: AW.BankAccount.AvailableFunds): string {
        return this.helperService.localizeNumberWithCurrency(baFunds.pendingFunds / 100, 2, 2, baFunds.currency);
    }

    formatPayoutDate(baFunds: AW.BankAccount.AvailableFunds): string {
        return dayjs.unix(baFunds.estimatedPayoutDate).format('Do MMM');
    }

    async getActions() {
        if (!this.isActionsEnabled()) return;
        this.actionLoading.set(true);
        this.actions = await this.actionService.getActionsFromBackend({ showAdvices: true, showAlerts: true, showClosed: false }).then(response => {
            this.actionLoading.set(false);
            const actions: AW.Operator.Action.Action[] = this.actionService.convertActionsToArray(response);
            this.isSkeleton.set(false);
            actions.sort(SortService.byPropertiesOf(['log'], 'timestamp'));
            return this.filterActionsToOnlyThree(actions);
        });
    }

    showNoActionsBox(): boolean {
        return this.actionService.showNoActionsBox(this.actions, false);
    }

    filterActionsToOnlyThree(actions: AW.Operator.Action.Action[]): AW.Operator.Action.Action[] {
        let index = 0;
        const filteredActions: AW.Operator.Action.Action[] = [];
        for (const action of actions) {
            if (action.active_actions) {
                if (index < 3) {
                    index++;
                    action.item_number = index;
                    filteredActions.push(action);
                }
            }
        }
        return filteredActions;
    }

    updateRevenueOverPeriod() {
        if (!this.dataSets) return;

        switch (this.viewOptionForCharts) {
            case 'week': {
                let sum = 0;
                this.dataSets.last7Days.line.forEach(elem => {
                    sum += !isNaN(elem.amount) ? elem.amount : 0;
                });
                sum = sum / 100;
                this.totalRevenue = this.helperService.localizeNumberWithCurrency(sum ? sum : 0);
                break;
            }
            case 'month': {
                let sum = 0;
                this.dataSets.last30Days.line.forEach(elem => {
                    sum += !isNaN(elem.amount) ? elem.amount : 0;
                });
                sum = sum / 100;
                this.totalRevenue = this.helperService.localizeNumberWithCurrency(sum ? sum : 0);
                break;
            }
            case 'year': {
                let sum = 0;
                this.dataSets.last12Months.line.forEach(elem => {
                    sum += !isNaN(elem.amount) ? elem.amount : 0;
                });
                sum = sum / 100;
                this.totalRevenue = this.helperService.localizeNumberWithCurrency(sum ? sum : 0);
                break;
            }
        }
    }

    openQuickPayoutModal(modal: TemplateRef<unknown>) {
        if (this.availableFunds().length === 0) return;
        this.mixpanelService.track('QUICK_PAYOUT_OPENED', { source: 'home_page' });
        const ref = this.awModal.open(modal);
        const sub = ref.closed.subscribe(() => {
            this.mixpanelService.track('QUICK_PAYOUT_DISMISSED', { source: 'home_page' });
            sub.unsubscribe();
        });
    }
}
